/**
 * Stock warning alerts for batch allocation. Two variants:
 *
 * - `StockWarningInClinic` - used by the batch editor when dispensing
 *   in-clinic. Links to the inventory page to add stock.
 * - `StockWarningRemote` - used by the prescription form for remote
 *   dispensing. Advisory messaging for the dispensing team.
 */
import { AlertTriangle, Package } from 'lucide-react'

import { Alert, AlertDescription } from '@/frontend/components/ui/alert'
import type { StockAllocationStatus } from '@/frontend/lib/stock'
import { routes } from '@/frontend/routes'

const linkClass = 'underline underline-offset-2'

interface StockWarningInClinicProps {
  stockStatus: StockAllocationStatus | null
  clinicId?: string
  productName?: string
  className?: string
}

function InClinicActions({
  clinicId,
  productName,
}: Pick<StockWarningInClinicProps, 'clinicId' | 'productName'>) {
  const inventoryUrl =
    clinicId && productName
      ? `${routes.inventory}?clinic=${clinicId}&q=${encodeURIComponent(productName)}`
      : null

  return (
    <>
      {inventoryUrl ? (
        <a href={inventoryUrl} target="_blank" rel="noreferrer" className={linkClass}>
          add stock via inventory
        </a>
      ) : (
        'add stock via inventory'
      )}
    </>
  )
}

/**
 * Stock warning for in-clinic dispensing. Shows actionable messages with
 * inventory links. Returns null when stock is ok.
 */
export function StockWarningInClinic({
  stockStatus,
  clinicId,
  productName,
  className,
}: StockWarningInClinicProps) {
  if (!stockStatus || stockStatus === 'ok') return null

  switch (stockStatus) {
    case 'no-stock':
      return (
        <Alert variant="destructive" className={className}>
          <AlertTriangle className="size-4" />
          <AlertDescription className="text-wrap">
            <p>
              Cannot dispense product. No stock history exists for this product at this clinic. If
              this is incorrect, <InClinicActions clinicId={clinicId} productName={productName} />.
            </p>
          </AlertDescription>
        </Alert>
      )

    case 'no-valid-stock':
      return (
        <Alert variant="warning" className={className}>
          <AlertTriangle className="size-4" />
          <AlertDescription className="text-wrap">
            <p>
              No valid stock is available - batches may be expired or depleted. Select a batch below
              to dispense against it anyway, or{' '}
              <InClinicActions clinicId={clinicId} productName={productName} />.
            </p>
          </AlertDescription>
        </Alert>
      )

    case 'insufficient-stock':
      return (
        <Alert variant="warning" className={className}>
          <Package className="size-4" />
          <AlertDescription className="text-wrap">
            <p>
              Insufficient stock to cover this quantity. Select a batch below to dispense against it
              anyway, or <InClinicActions clinicId={clinicId} productName={productName} />.
            </p>
          </AlertDescription>
        </Alert>
      )

    case 'mixed-unbatched':
      return (
        <Alert variant="warning" className={className}>
          <AlertTriangle className="size-4" />
          <AlertDescription className="text-wrap">
            <p>
              Sufficient stock exists but some or all of it has no batch number or expiry date. This
              product is usually dispensed with a batch number - select a batch below to dispense
              against it anyway.
            </p>
          </AlertDescription>
        </Alert>
      )
  }
}

interface StockWarningRemoteProps {
  stockStatus: StockAllocationStatus | null
  clinicName?: string
}

/**
 * Stock warning for remote dispensing. Advisory messages aimed at the
 * prescriber - the dispensing team will handle allocation. Returns null
 * when stock is ok.
 */
export function StockWarningRemote({ stockStatus, clinicName }: StockWarningRemoteProps) {
  if (!stockStatus || stockStatus === 'ok') return null

  const clinic = clinicName ?? 'the selected clinic'

  switch (stockStatus) {
    case 'no-stock':
      return (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertDescription className="text-wrap flex flex-col gap-1">
            <p>
              No stock exists for this product at {clinic}. Before prescribing, please check whether
              a duplicate version of this product exists with valid stock at {clinic}.
            </p>
          </AlertDescription>
        </Alert>
      )

    case 'no-valid-stock':
      return (
        <Alert variant="warning">
          <AlertTriangle className="size-4" />
          <AlertDescription className="text-wrap">
            <p>Stock exists at {clinic} but all batches are expired or depleted.</p>
          </AlertDescription>
        </Alert>
      )

    case 'insufficient-stock':
      return (
        <Alert variant="warning">
          <Package className="size-4" />
          <AlertDescription className="text-wrap">
            <p>{clinic} may not have enough stock to fulfil the full quantity.</p>
          </AlertDescription>
        </Alert>
      )

    case 'mixed-unbatched':
      return null
  }
}
